import React, { useRef, useCallback, useEffect } from 'react'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { useCardFlyAnimation } from './hooks/useCardFlyAnimation'
import { getCardDimensions } from './hooks/getCardDimensions'

export type CardAnimationPhase = 'idle' | 'jiggling' | 'transitioning' | 'landed'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

export type OracleCardGreetingVariant = 'greeting' | 'overlay'

interface OracleCardGreetingProps {
  onPull?: () => void
  backImageUrl?: string
  animationPhase: CardAnimationPhase
  frontImageUrl?: string
  onTransitionEnd?: () => void
  variant?: OracleCardGreetingVariant
  contentAreaRef?: React.RefObject<HTMLDivElement | null>
}

const DECK_CARD_INDICES = [0, 1, 2, 3, 4, 5, 6, 7] as const

export const OracleCardGreeting: React.FC<OracleCardGreetingProps> = ({
  onPull,
  backImageUrl,
  animationPhase,
  frontImageUrl,
  onTransitionEnd,
  variant = 'greeting',
  contentAreaRef,
}) => {
  const greetingText = useTranslatedText({ keys: ['oracleCard', 'greeting'] })
  const clickToPullText = useTranslatedText({ keys: ['oracleCard', 'clickToPull'] })

  const imageContainerRef = useRef<HTMLDivElement>(null)
  const flipperRef = useRef<HTMLDivElement>(null)
  const capturedRectRef = useRef<Rect | null>(null)

  // Drive the fly animation when transitioning
  const handleFlyComplete = useCallback(() => {
    onTransitionEnd?.()
  }, [onTransitionEnd])

  useCardFlyAnimation({
    active: animationPhase === 'transitioning',
    moverRef: imageContainerRef,
    flipperRef,
    onComplete: handleFlyComplete,
  })

  // Overlay variant: compute a content-area-centered rect when animation starts
  useEffect(() => {
    if (variant !== 'overlay') return
    if (animationPhase === 'idle' || animationPhase === 'landed') return
    if (capturedRectRef.current) return
    const isMobile = window.innerWidth < 1025
    const area = contentAreaRef?.current?.getBoundingClientRect()
    const areaTop = area?.top ?? 0
    const areaLeft = area?.left ?? 0
    const areaWidth = area?.width ?? window.innerWidth
    const areaHeight = area?.height ?? window.innerHeight
    const cardDims = getCardDimensions(areaWidth, isMobile, areaHeight)
    const jiggleScale = isMobile ? 0.7 : 1
    const cardWidth = cardDims.width * jiggleScale
    const cardHeight = cardDims.height * jiggleScale
    capturedRectRef.current = {
      top: areaTop + (areaHeight - cardHeight) / 2,
      left: areaLeft + (areaWidth - cardWidth) / 2,
      width: cardWidth,
      height: cardHeight,
    }
  }, [variant, animationPhase, contentAreaRef])

  // Reset captured rect when phase returns to idle
  useEffect(() => {
    if (animationPhase === 'idle') {
      capturedRectRef.current = null
    }
  }, [animationPhase])

  const handlePullClick = () => {
    // Capture the image container's screen rect before it goes fixed
    if (imageContainerRef.current) {
      const r = imageContainerRef.current.getBoundingClientRect()
      capturedRectRef.current = {
        top: r.top, left: r.left, width: r.width, height: r.height,
      }
    }
    onPull()
  }

  // When landed, the real card is visible underneath — hide entirely
  if (animationPhase === 'landed') {
    return null
  }

  const isAnimating = animationPhase !== 'idle'
  const isJiggling = animationPhase === 'jiggling'

  // Build image container style: in-flow when idle, fixed when animating
  const rect = capturedRectRef.current
  const imageContainerStyle: React.CSSProperties | undefined = isAnimating && rect
    ? {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    }
    : undefined

  const isOverlayEntering = variant === 'overlay' && isJiggling

  const isShuffling = animationPhase === 'idle' && variant === 'greeting' && backImageUrl

  const imageContainerClass = [
    'oracle-card-greeting__image-container',
    isAnimating ? 'oracle-card-greeting__image-container--fixed' : '',
    isOverlayEntering ? 'oracle-card-greeting__image-container--entering' : '',
    isShuffling ? 'oracle-card-greeting__image-container--shuffling' : '',
  ].filter(Boolean).join(' ')

  const flipperClass = [
    'oracle-card-greeting__flipper',
    isJiggling ? 'oracle-card-greeting__flipper--jiggling' : '',
  ].filter(Boolean).join(' ')

  // Overlay variant: render only the animating card (no greeting text, no button)
  if (variant === 'overlay') {
    if (!isJiggling && animationPhase !== 'transitioning') return null
    return (
      <div
        className={imageContainerClass}
        ref={imageContainerRef}
        style={imageContainerStyle}
      >
        <div className={flipperClass} ref={flipperRef}>
          <img
            className="oracle-card-greeting__image"
            src={backImageUrl}
            alt=""
            decoding="sync"
          />
          {frontImageUrl && (
            <img
              className="oracle-card-greeting__front-image"
              src={frontImageUrl}
              alt=""
            />
          )}
        </div>
      </div>
    )
  }

  const greetingClass = [
    'oracle-card-greeting',
    isAnimating ? 'oracle-card-greeting--animating' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={greetingClass}>
      {!isAnimating && (
        <p className="oracle-card-greeting__description">{greetingText}</p>
      )}
      <button
        type="button"
        className="oracle-card-greeting__pull-button"
        onClick={handlePullClick}
        disabled={isAnimating}
      >
        {animationPhase === 'idle' && backImageUrl && (
          <div className="oracle-card-greeting__deck" aria-hidden="true">
            {DECK_CARD_INDICES.map((i) => (
              <img
                key={i}
                className={`oracle-card-greeting__deck-card oracle-card-greeting__deck-card--${i}`}
                src={backImageUrl}
                alt=""
                draggable={false}
              />
            ))}
          </div>
        )}
        {backImageUrl && (
          <div
            className={imageContainerClass}
            ref={imageContainerRef}
            style={imageContainerStyle}
          >
            <div className={flipperClass} ref={flipperRef}>
              <img
                className="oracle-card-greeting__image"
                src={backImageUrl}
                alt=""
                decoding="sync"
              />
              {frontImageUrl && (
                <img
                  className="oracle-card-greeting__front-image"
                  src={frontImageUrl}
                  alt=""
                />
              )}
                </div>
          </div>
        )}
        {!isAnimating && (
          <span className="oracle-card-greeting__pull-label">{clickToPullText}</span>
        )}
      </button>
    </div>
  )
}
